/** 通道管理：只读列出 `GET /channels`，最近对账与数据源取自既有端点。无任何写动作。 */

import { useQuery } from "@tanstack/react-query";

import { useSnapshots, useStatus } from "../../api/queries";
import { isRetryable } from "../../api/errors";
import { Card, Lede, Note, PageHead } from "../../components/Card";
import { Pill } from "../../components/Pill";
import { Table } from "../../components/Table";
import { useChannels, type ChannelRecord } from "../../lib/channels";
import { ScreenState } from "../p2/Common";
import { readReconList, verdict, type ReconRow } from "../reconciliation/api";

const TONE = { ok: "ok", failed: "bad", not_wired: "neutral", unknown: "warn" } as const;

export function ChannelsScreen(): React.ReactNode {
  const channels = useChannels();
  const status = useStatus();
  const halted = status.data?.halted === true;
  return (
    <>
      <PageHead title="通道管理" sub="只读 · GET /channels" />
      <Lede>
        通道状态、是否在用全部由后端给出。{halted ? "停机中：任何通道都不下单。" : ""}
      </Lede>
      <ScreenState query={channels} what="通道清单（GET /channels）">
        {(data) => {
          const active = data.channels.find((c) => c.channel_id === data.active_channel_id);
          return (
            <>
              {active?.status === "failed" ? (
                <Note tone="bad">在用通道不可用：{active.status_detail}（详见降级记录屏）</Note>
              ) : null}
              <Card title="通道列表" sub={`截至 ${data.as_of_utc}`} nopad>
                <Table
                  cols={[
                    { label: "通道", mono: true },
                    { label: "名称" },
                    { label: "适配层", mono: true },
                    { label: "venue", mono: true },
                    { label: "在用" },
                    { label: "状态" },
                    { label: "最近检查", wrap: true },
                    { label: "最近切换 / 降级", wrap: true },
                  ]}
                  rows={data.channels.map((c) => ({
                    key: c.channel_id,
                    cells: [
                      c.channel_id,
                      c.label,
                      c.adapter,
                      c.venue,
                      c.active ? <Pill tone="ok">在用</Pill> : "—",
                      <Pill tone={TONE[c.status]}>{c.status}</Pill>,
                      checkText(c),
                      c.last_event_id ? `${c.last_event_id} · ${c.last_event_utc ?? ""}` : "尚无切换或降级记录",
                    ],
                  }))}
                />
              </Card>
              {data.channels.length === 1 && data.channels[0]?.channel_id === "kernel-self" ? (
                <Note>现阶段只有纸上盘自撮合一条通道；真券商通道待接入（D4 实盘通道未裁决）。</Note>
              ) : null}
            </>
          );
        }}
      </ScreenState>
      <div className="grid g2" style={{ marginTop: 12 }}>
        <ReconCard />
        <SourceCard />
      </div>
    </>
  );
}

function checkText(c: ChannelRecord): string {
  if (c.last_check_utc) return `${c.last_check_utc} · ${c.last_check_ok === false ? "失败" : "通过"}`;
  return c.channel_id === "kernel-self" ? "无外部连接，不做连通检查" : "还没检查过";
}

function ReconCard(): React.ReactNode {
  // 后端 `GET /reconciliations` 是裸数组，与对账屏共用同一个解析（以后端形状为准）。
  const recon = useQuery<ReconRow[], Error>({
    queryKey: ["reconciliations"],
    queryFn: readReconList,
    retry: (count: number, error: unknown) => count < 2 && isRetryable(error),
    retryDelay: 1_500,
  });
  return (
    <Card title="最近对账" sub="GET /reconciliations">
      <ScreenState query={recon} what="对账记录">
        {(rows) => {
          const last = rows[rows.length - 1];
          if (!last) return <div className="empty">还没对过账</div>;
          const v = verdict(last);
          return (
            <dl className="kv">
              <dt>run_id</dt>
              <dd>{last.run_id}</dd>
              <dt>结果</dt>
              <dd>
                <Pill tone={v.tone}>{v.text}</Pill>
              </dd>
              <dt>核对笔数</dt>
              <dd>{last.checked ?? <span className="faint">缺</span>}</dd>
              <dt>摘要</dt>
              <dd>{last.summary ?? <span className="faint">缺</span>}</dd>
            </dl>
          );
        }}
      </ScreenState>
    </Card>
  );
}

function SourceCard(): React.ReactNode {
  const snaps = useSnapshots();
  return (
    <Card title="数据源" sub="GET /snapshots（只读来源名）">
      <ScreenState query={snaps} what="数据快照">
        {(data) => {
          const sources = [...new Set(data.snapshots.map((s) => s.source))];
          return sources.length === 0 ? (
            <div className="empty">还没有数据快照</div>
          ) : (
            <div>{sources.map((s) => <Pill key={s}>{s}</Pill>)}</div>
          );
        }}
      </ScreenState>
    </Card>
  );
}
